
import React, { createContext, useContext, useState, useEffect } from "react";

type AccessibilitySettings = {
  highContrast: boolean;
  largeText: boolean;
  reducedMotion: boolean;
  screenReader: boolean;
};

type AccessibilityContextType = {
  settings: AccessibilitySettings;
  updateSetting: (setting: keyof AccessibilitySettings, value: boolean) => void;
  resetSettings: () => void;
};

const defaultSettings: AccessibilitySettings = {
  highContrast: false,
  largeText: false,
  reducedMotion: false,
  screenReader: false,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    // Load settings from localStorage if available
    const savedSettings = localStorage.getItem("accessibility-settings");
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });

  // Update settings in localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("accessibility-settings", JSON.stringify(settings));
    
    // Apply settings to the document
    document.documentElement.classList.toggle("high-contrast-mode", settings.highContrast);
    document.documentElement.classList.toggle("large-text-mode", settings.largeText);
    document.documentElement.classList.toggle("reduced-motion-mode", settings.reducedMotion);
    
    // Add prefers-reduced-motion media query styles when enabled
    if (settings.reducedMotion) {
      document.body.style.setProperty("--transition-speed", "0s");
    } else {
      document.body.style.removeProperty("--transition-speed");
    }
  }, [settings]);

  const updateSetting = (setting: keyof AccessibilitySettings, value: boolean) => {
    setSettings((prev) => ({ ...prev, [setting]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <AccessibilityContext.Provider value={{ settings, updateSetting, resetSettings }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error("useAccessibility must be used within an AccessibilityProvider");
  }
  return context;
}
